// AssignmentBlock — embedded inside PublishToLessonModal and the class Assignments tab.
//
// While the teacher is publishing a topic, this optional section lets
// them attach a single assignment that students will see grouped by
// subject in /student/assignments and at the bottom of the topic page.
// Renders disabled by default; the teacher opts in with a toggle.
import React from 'react';
import { ClipboardList } from 'lucide-react';
import TimeSelect12h from './ui/TimeSelect12h';
import { INPUT, FIELD_LABEL } from './teacher/teacherUi';

export default function AssignmentBlock({
  enabled, onToggle,
  title, description, dueDate, dueTime, points,
  onChange, hideToggle = false,
}) {
  return (
    <div
      className={hideToggle ? '' : `rounded-xl border overflow-hidden transition-colors ${enabled ? 'border-[#1A7F37]' : 'border-slate-200'} bg-white`}
      data-testid="assignment-block"
    >
      {/* Toggle header — omitted when the surrounding UI already implies
          "this form creates an assignment" (e.g. a standalone Assignments
          tab), so hideToggle skips straight to the fields. */}
      {!hideToggle && (
        <label className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-slate-50">
          <span className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${enabled ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-500'}`}>
            <ClipboardList className="w-4 h-4" />
          </span>
          <span className="flex-1 min-w-0">
            <span className="block text-sm font-medium text-slate-900">
              Attach an assignment <span className="font-normal text-slate-500">(optional)</span>
            </span>
            <span className="block text-sm text-slate-500">
              Students see it in Assignments, grouped by subject, with its due date
            </span>
          </span>
          <input type="checkbox" checked={enabled} onChange={(e) => onToggle(e.target.checked)}
            data-testid="assignment-toggle"
            className="w-4 h-4 accent-[#1A7F37] flex-shrink-0" />
        </label>
      )}

      {/* Form (visible only when enabled) */}
      {enabled && (
        <div className={hideToggle ? 'space-y-4' : 'px-4 pb-4 pt-4 space-y-4 border-t border-slate-100'}>
          <Field label="Assignment title" required>
            <input value={title} onChange={(e) => onChange({ title: e.target.value })}
              placeholder="e.g. Solve Q1 to Q10 from the Chapter 4 worksheet"
              data-testid="assignment-title"
              className={INPUT} />
          </Field>
          <Field label="Instructions">
            <textarea value={description} onChange={(e) => onChange({ description: e.target.value })}
              placeholder="Pages to do, what to hand in, where to share results"
              data-testid="assignment-description"
              rows={2}
              className={`${INPUT} !h-auto py-2 resize-none`} />
          </Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Due date" required>
              <input type="date" value={dueDate} onChange={(e) => onChange({ dueDate: e.target.value })}
                data-testid="assignment-due-date"
                className={INPUT} />
            </Field>
            <Field label="Due time">
              <TimeSelect12h value={dueTime} onChange={(v) => onChange({ dueTime: v })} allowEmpty testId="assignment-due-time" />
            </Field>
            <Field label="Points">
              <input type="number" min={0} max={1000} value={points}
                onChange={(e) => onChange({ points: Math.max(0, Number(e.target.value) || 0) })}
                data-testid="assignment-points"
                className={`${INPUT} tabular-nums`} />
            </Field>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, required = false, children }) {
  return (
    <label className="block">
      <span className={FIELD_LABEL}>
        {label}{required && <span className="text-red-600" aria-hidden="true"> *</span>}
      </span>
      {children}
    </label>
  );
}
